import { Action } from '@Context/actions/action';
import { Actions } from '@Context/common/general/context';
import { ActionError } from '@Context/common/general/initial-state.constants';
import storage from '@Context/storage';
import Event from '@Helpers/event';
import { resolveFirebaseFunctionName } from "@Runtime/firebase-function-name";
import * as Sentry from "@sentry/react";
import { httpsCallable } from 'firebase/functions';
import { lowerCase, toLower } from "lodash-es";

export interface InvokeProps {
  name: string, 
  payload?: any, 
  disableLoaders?: boolean
}

class BaseApiAction extends Action {
  _errorHandler = (error?:any) => {};
  _retryHandler = new Event();
  _retrySuccess = ()=>{};
  _autoRetryLimit = 3;
  _autoRetryDelay = 500;
  _autoRetryCount = 0;
  _functionName = "";

  invokeCloudFunction({ name, payload = {} }: InvokeProps) {
    const storageKey = `${name}_${JSON.stringify(payload)}`;
    this._functionName = name;
    // Enable loader

    Sentry.addBreadcrumb({
      category: 'base-apiaction',
      message: `Invoking cloud function: ${name}`,
      data: payload
    });

    let promiseResolve: (value?:any)=>void;
    const loadPromise = new Promise((resolve) => {
      promiseResolve = resolve;
    });
    if (this._disableLoaders !== true) {
      this._state.loading.events.enableLoader.emit(loadPromise);
    }

    const successHandler = (response: any) => {
      if(this._retrySuccess) this._retrySuccess();
      if (this._disableLoaders !== true) {
        promiseResolve();
        this._dispatch({
          type: Actions.CoreActions.UpdateState,
          payload: {
            loading: { active: false },
          },
        });
      }

      const code = response.data?.code;
      const isCorrectResponseFormat = code && lowerCase(code) === 'ok';
      const data = isCorrectResponseFormat ? response.data?.data : response.data;
      this.onSuccess(data);
    };

    const errorHandler = (error: any) => {

      Sentry.captureException(error, {
        tags: {
          type: "base-api-action",
          action_name: this.constructor.name,
          function_name: this._functionName,
          error_code: error.code,
          error_status: error.status,
          error_message: error.message,
          error_details: error.details || {},
        },
        fingerprint: [
          this.constructor.name,
          error.code,
          error.status,
          error.message
        ]
      });
      Sentry.flush();
      console.log("Sentry error log sent for function", this.constructor.name);

      const getActionError = (error: any) => {
        switch(toLower(error.code)) {
          // Error message only
          case 'functions/data-loss':
          case 'functions/not-found':
          case 'functions/already-exists':
          case 'functions/failed-precondition':
          case 'functions/aborted':
          case 'functions/out-of-range':
            return ActionError.MessageOnly;
          // Back navigation error
          case 'functions/invalid-argument':
          case 'functions/permission-denied':
            return ActionError.GoBack;
          // Retry Errors
          case 'functions/cancelled':
          case 'functions/unknown':
          case 'functions/resource-exhausted':
          case 'functions/deadline-exceeded':
          case 'functions/internal':
          case 'functions/unavailable':
            return ActionError.Retry;
          // Force Home Navigation
          // Force Logout Errors
          case 'functions/unauthenticated':
          case 'functions/unimplemented':
            return ActionError.Logout;
          default: 
          return ActionError.Retry;
          break;
      }
    }
      // // Auto retry failed calls
      // if(this._autoRetryCount < this._autoRetryLimit) {
      //   setTimeout(()=>{
      //     this.runAction();
      //   }, this._autoRetryDelay);
      //   return;
      // }

      this._retryHandler.once(()=>{
        console.log("Retry handler called");
      });
      this._retryHandler.once((retrySuccess = () => {})=>{ 
        this._retrySuccess = retrySuccess;
        this.runAction() 
      } );
      console.warn(error);
      // Send error log to analytics services
      // this._dispatch({
      //   type: Actions.AppActions.AnalyticsEvent,
      //   payload: {
      //     type: AnalyticsEvents.error_occured,
      //     data: error
      //   }
      // });

      if (this._disableLoaders !== true){
        promiseResolve();
      } else {
        this._dispatch({
          type: Actions.CoreActions.UpdateState,
          payload: {
            loading: { active: false },
          },
        });
      }

      if( this._onError ) {
        return this._onError(error);
      }

      this._dispatch({
        type: Actions.CoreActions.UpdateState,
        payload: {
          error: {
            active: true,
            action: `action.${name}`,
            message: error.message,
            type: getActionError(error),
            retryHandler: this._retryHandler
          }
        },
      });
    };
    this._errorHandler = errorHandler;

    // Try to retrieve cached response from storage only for calls that explicitly opt into caching.
    const storedData = this._cacheTime ? storage.get({ key: storageKey }) : null;
    if (storedData) return successHandler({ data: storedData });

    // Else run call the cloud function normally
      httpsCallable(this._functions, resolveFirebaseFunctionName(name))(payload)
      .then((response) => {
        // Add response to storage if cache is configured in payload
        if (this._cacheTime) {
          storage.set({
            key: storageKey,
            data: response.data || {},
            expiry: this._cacheTime,
            session_only: this._payload.session_only || false,
          });
        }
        successHandler(response)
      })
      .catch(errorHandler);
  }
};

export default BaseApiAction;
